import { Box, alpha } from '@mui/material';
import { getCrmPalette } from '../../theme/brand';

/**
 * Gustavo asomándose de la burbuja, en grande — la misma figura del botón flotante.
 *
 * (26/08/2026) La lámina de anuncio abría con la foto cuadrada de S3 metida en un círculo,
 * y no es la imagen con la que los agentes lo reconocen: la que ven todo el día es el
 * RECORTE sin fondo (`gustavo-cutout.png`) saliendo de la burbuja, con la cabeza afuera y
 * la luz girando sobre el borde.
 *
 * La geometría es la de `GustavoAvatarFab` (60×78) escalada por `size/60`. Está copiada y
 * no compartida a propósito: el FAB vive en producción y arrastra tilt de mouse, gestos,
 * badge de avisos y un loop de saltitos que en una lámina estática no van. Lo que importa
 * que coincida —la máscara, las proporciones y el giro— está parametrizado por `k`, así
 * que cambia de tamaño sin deformarse.
 *
 * El color sale de la MARCA ACTIVA, igual que el FAB: dorado en Imperium, celeste en
 * Booster, el suyo en cada white-label.
 */
export function GustavoBurbuja({ size = 140, alt = 'Gustavo' }: { size?: number; alt?: string }) {
  const k = size / 60;
  const MARCA = getCrmPalette().brand;
  const conMarca = (a: number) => alpha(MARCA.golden, a);

  // Disco del círculo (cuerpo adentro) ∪ elipse de la cabeza (asoma arriba).
  const mask =
    `radial-gradient(circle ${28 * k}px at ${30 * k}px ${48 * k}px, #000 ${27 * k}px, transparent ${28 * k}px), ` +
    `radial-gradient(${12 * k}px ${25 * k}px at ${30 * k}px ${11 * k}px, #000 60%, transparent 100%)`;

  return (
    <Box aria-hidden={false} sx={{
      position: 'relative', width: size, height: 78 * k, flexShrink: 0,
      animation: 'gburb-float 6s ease-in-out infinite',
      '@keyframes gburb-float': { '0%,100%': { transform: 'translateY(0)' }, '50%': { transform: `translateY(-${4 * k}px)` } },
      '@keyframes gburb-glow':  { '0%,100%': { opacity: 0.55, transform: 'scale(1)' }, '50%': { opacity: 0.85, transform: 'scale(1.06)' } },
      '@keyframes gburb-spin':  { to: { transform: 'rotate(360deg)' } },
      '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
    }}>
      {/* halo que late, detrás de todo */}
      <Box aria-hidden sx={{
        position: 'absolute', left: 0, bottom: 0, width: size, height: size, borderRadius: '50%',
        pointerEvents: 'none', filter: `blur(${6 * k}px)`,
        background: `radial-gradient(circle, ${conMarca(0.4)}, ${conMarca(0.06)} 48%, transparent 70%)`,
        animation: 'gburb-glow 3.6s ease-in-out infinite',
        '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
      }} />

      {/* la burbuja: borde de la marca e interior oscuro */}
      <Box aria-hidden sx={{
        position: 'absolute', left: 0, bottom: 0, width: size, height: size, borderRadius: '50%',
        border: `${Math.max(2, 2 * k * 0.7)}px solid ${MARCA.golden}`,
        background: 'radial-gradient(circle at 50% 40%, #201c15, #0e0d0b 80%)',
        boxShadow: `0 ${8 * k}px ${20 * k}px rgba(0,0,0,.5), 0 0 ${16 * k}px ${conMarca(0.28)}`,
      }} />

      {/* la luz que VIAJA sobre la línea del círculo */}
      <Box aria-hidden sx={{
        position: 'absolute', left: -2 * k, bottom: -2 * k, width: size + 4 * k, height: size + 4 * k,
        borderRadius: '50%', pointerEvents: 'none',
        background: `conic-gradient(from 0deg, transparent 0deg, ${alpha(MARCA.goldenLight, 0.9)} 22deg, transparent 55deg, transparent 180deg, ${conMarca(0.75)} 202deg, transparent 235deg)`,
        animation: 'gburb-spin 3.6s linear infinite',
        WebkitMask: `radial-gradient(farthest-side, transparent calc(100% - ${3 * k}px), #000 calc(100% - ${3 * k}px))`,
        mask: `radial-gradient(farthest-side, transparent calc(100% - ${3 * k}px), #000 calc(100% - ${3 * k}px))`,
        '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
      }} />

      {/* el personaje: cuerpo dentro del círculo, cabeza asomando */}
      <Box sx={{
        position: 'absolute', inset: 0, pointerEvents: 'none',
        WebkitMaskImage: mask, maskImage: mask,
        WebkitMaskRepeat: 'no-repeat', maskRepeat: 'no-repeat',
      }}>
        <Box component="img" src="/gustavo-cutout.png" alt={alt} draggable={false} sx={{
          position: 'absolute', left: '50%', bottom: 0,
          marginLeft: `-${22 * k}px`, width: 44 * k, height: 'auto',
          userSelect: 'none', filter: `drop-shadow(0 ${3 * k}px ${5 * k}px rgba(0,0,0,.5))`,
        }} />
      </Box>
    </Box>
  );
}
